import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import PasswordRequirements, {
  PASSWORD_REQUIREMENTS_ID,
} from '../components/auth/PasswordRequirements';
import { useAuthContext } from '../contexts/AuthContext';
import { authErrorMessage } from '../utils/errorMessages';
import { checkPassword } from '../utils/passwordRule';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updatePassword } = useAuthContext();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const email = useMemo(() => searchParams.get('email') || '', [searchParams]);
  const resetPasswordToken = useMemo(
    () => searchParams.get('resetPasswordToken') || searchParams.get('token') || '',
    [searchParams],
  );

  const hasValidQuery = !!email && !!resetPasswordToken;
  const passwordCheck = useMemo(() => checkPassword(password), [password]);

  const handleSubmit = async event => {
    event.preventDefault();
    if (!passwordCheck.ok) {
      setError('Please choose a password that meets all the requirements below.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await updatePassword({
        email,
        newPassword: password,
        resetPasswordToken,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    } catch (updateError) {
      setError(authErrorMessage(updateError, 'Unable to update password. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasValidQuery) {
    return (
      <AuthLayout title="Reset Password" subtitle="This reset link is invalid or incomplete.">
        <Link to="/forgot-password" className="text-red-400 hover:text-red-300 text-sm">
          Request a new reset link
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset Password" subtitle={`Updating password for ${email}`}>
      {success ? (
        <div className="rounded-lg border border-green-900 bg-green-950/30 p-4 text-sm text-green-300">
          Password updated. Redirecting to sign in...
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="password"
              placeholder="New password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
              aria-describedby={PASSWORD_REQUIREMENTS_ID}
              className="w-full rounded-lg bg-secondary border border-gray-border px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent"
            />
            <div className="mt-2">
              <PasswordRequirements password={password} />
            </div>
          </div>
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={event => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            className="w-full rounded-lg bg-secondary border border-gray-border px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent"
          />

          {error && (
            <div className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !passwordCheck.ok}
            className="w-full rounded-lg bg-accent text-primary hover:bg-accent-hover disabled:opacity-60 disabled:cursor-not-allowed px-4 py-3 font-semibold transition-colors"
          >
            {submitting ? 'Updating...' : 'Update password'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ResetPasswordPage;
